import { QueryClient, SetDataOptions, notifyManager } from "@tanstack/react-query";
import { QueryMethodForModel, ModelName, TellescopeQueryUpdate, TellescopeQueryUpdateWithTimestamp } from "../../types";
import { TellescopeMethodQueryFilterConfig, TellescopeMethodQueryUpdateConfig, TellescopeUpdaterFn } from "../../hooks";
import { getTellescopeModelQueriesData, GetTellescopeModelQueriesDataArgs } from "./get-tellescope-model-queries-data";
import { setTellescopeQueryData } from "./set-tellescope-query-data";
import { parseTellescopeQueryKey } from "../parse-tellescope-query-key";

/**
 * Arguments for setting data for all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface SetTellescopeModelQueriesDataArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to set queries data for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The data to set, or an updater function */
  updater?: TellescopeUpdaterFn<Model, QueryMethodForModel<Model>>;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: TellescopeMethodQueryFilterConfig<Model, QueryMethodForModel<Model>, TError>;
  /** Optional set data options (e.g., updatedAt) applied to all set operations */
  options?: SetDataOptions;
  /**
   * Optional method-specific settings. Allows setting data for specific methods
   * and optionally matching by request parameters.
   */
  methodConfigs?: Partial<{
    [Method in QueryMethodForModel<Model>]: TellescopeMethodQueryUpdateConfig<Model, Method> & {
      /** Optional method-specific set data options that override global options */
      options?: SetDataOptions;
    };
  }>;
  /** Whether this update is being applied optimistically (before mutation completes). Defaults to false. */
  isOptimistic?: boolean;
}

type MethodConfigsEntryKey<Model extends ModelName> = QueryMethodForModel<Model>;
type MethodConfigsEntryValue<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> = TellescopeMethodQueryUpdateConfig<Model, Method, TError> & {
  /** Optional method-specific set data options that override global options */
  options?: SetDataOptions;
};
type MethodConfigsEntry<Model extends ModelName, TError = Error> = {
  [Method in QueryMethodForModel<Model>]: [
    MethodConfigsEntryKey<Model>,
    MethodConfigsEntryValue<Model, Method, TError>,
  ];
}[QueryMethodForModel<Model>];

/**
 * Return type for setting data for all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name
 */
export interface SetTellescopeModelQueriesDataReturn<Model extends ModelName> {
  /** Array of queries that were updated with their original data before the update */
  originalQueries: TellescopeQueryUpdateWithTimestamp<Model, QueryMethodForModel<Model>>[];
  /** Array of queries that were updated with their new data after the update */
  updatedQueries: TellescopeQueryUpdate<Model, QueryMethodForModel<Model>>[];
}

/**
 * Sets cached data for all queries for a specific Tellescope model.
 *
 * This function provides a way to set data for all queries for a model, optionally
 * with method-specific updaters and filters. When no methodConfigs are provided, it sets
 * data for all queries for the model. When methodConfigs are provided, it only sets
 * data for queries for the specified methods.
 *
 * **Special Updater Return Values:**
 * - Return `undefined`: Skip the update (no change to cache)
 * - Return `null`: Reset the query (remove from cache) - useful for deletions
 * - Return data: Update the cache with the new data
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, updater, and optional filters
 * @returns An object with originalQueries (before update) and updatedQueries (after update)
 *
 * @example
 * ```ts
 * // Set data for all user queries
 * const { originalQueries, updatedQueries } = setTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   updater: (old) => old ? { ...old, isActive: true } : old,
 * });
 * ```
 *
 * @example
 * ```ts
 * // Set data only for specific methods with method-specific updaters
 * const result = setTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   updater: (old) => old, // Default updater (no-op)
 *   methodConfigs: {
 *     getOne: {
 *       updater: (old) => old ? { ...old, lastViewed: Date.now() } : old,
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *       updater: (old) => old ? { ...old, isStale: true } : old,
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Reset queries for deleted entities
 * setTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   methodConfigs: {
 *     getOne: {
 *       request: { id: "deleted-user-123" },
 *       updater: () => null, // Resets the query
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters and options
 * setTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   updater: (old) => old ? { ...old, updated: true } : old,
 *   filters: {
 *     stale: true, // Only set data for stale queries
 *   },
 *   options: {
 *     updatedAt: Date.now(),
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use method-specific options
 * setTellescopeModelQueriesData({
 *   queryClient,
 *   model: "users",
 *   updater: (old) => old,
 *   methodConfigs: {
 *     getOne: {
 *       options: {
 *         updatedAt: Date.now(),
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const setTellescopeModelQueriesData = <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  updater,
  filters,
  methodConfigs,
  options,
  isOptimistic = false,
}: SetTellescopeModelQueriesDataArgs<Model, TError>): SetTellescopeModelQueriesDataReturn<Model> => {
  const methodNames = Object.keys(methodConfigs ?? {}) as QueryMethodForModel<Model>[];
  const methodConfigsEntries = Object.entries(methodConfigs ?? {}) as MethodConfigsEntry<Model, TError>[];
  const methodFilters = methodConfigsEntries.reduce<
    NonNullable<GetTellescopeModelQueriesDataArgs<Model, TError>["methodFilters"]>
  >((acc, [method, { updater: _, options: __, ...filter }]) => {
    // @ts-expect-error union type is too broad here
    acc[method] = filter;
    return acc;
  }, {});
  const queries = getTellescopeModelQueriesData<Model, TError>({
    model,
    queryClient,
    filters,
    methodFilters,
  });

  // All cache writes are wrapped in notifyManager.batch() so observer
  // notifications are coalesced into a single React render pass.

  // If no method filters are specified, set data for all queries for the model
  if (!methodConfigs || !methodNames.length) {
    return notifyManager.batch(() =>
      queries.allQueries.reduce<SetTellescopeModelQueriesDataReturn<Model>>(
        (acc, [queryKey]) => {
          const { model, method, request } = parseTellescopeQueryKey<Model, QueryMethodForModel<Model>>(queryKey);
          if (request === undefined) {
            return acc;
          }
          const { originalQuery, updatedQuery } = setTellescopeQueryData({
            model,
            method,
            request,
            queryClient,
            // @ts-expect-error - TypeScript can't narrow union types when QueryData could itself be a function
            updater: typeof updater === "function" ? (old) => updater(old, { queryKey, isOptimistic }) : updater,
            options,
          });

          acc.originalQueries.push(originalQuery);
          acc.updatedQueries.push(updatedQuery);
          return acc;
        },
        { originalQueries: [], updatedQueries: [] }
      )
    );
  }

  // Otherwise, set data for each specified method
  return notifyManager.batch(() =>
    methodNames.reduce<SetTellescopeModelQueriesDataReturn<Model>>(
      (acc, method) => {
        const methodQueries = queries.queriesByMethod[method];
        const methodUpdater = methodConfigs[method]?.updater ?? updater;
        const methodOptions = methodConfigs[method]?.options ?? options;

        return (
          methodQueries?.reduce<SetTellescopeModelQueriesDataReturn<Model>>((methodAcc, update) => {
            if (!update) {
              return acc;
            }
            const [queryKey] = update;
            const { model, method, request } = parseTellescopeQueryKey<Model, QueryMethodForModel<Model>>(queryKey);
            if (!request) {
              return acc;
            }
            const { originalQuery, updatedQuery } = setTellescopeQueryData<Model, QueryMethodForModel<Model>>({
              model,
              method,
              request,
              queryClient,
              // @ts-expect-error
              updater: (old) =>
                methodUpdater instanceof Function ? methodUpdater(old, { queryKey, isOptimistic }) : methodUpdater,
              options: methodOptions,
            });

            methodAcc.originalQueries.push(originalQuery);
            methodAcc.updatedQueries.push(updatedQuery);
            return methodAcc;
          }, acc) ?? acc
        );
      },
      { originalQueries: [], updatedQueries: [] }
    )
  );
};
